'use client';
import { LandingShell } from "@/components/landing/LandingShell";
import { useState } from 'react';
import { autoDir } from '@/lib/autoDir';
import api, { setToken } from '@/lib/api';
import { setStoredUser } from '@/lib/permissions';
import { useRouter } from 'next/navigation';
import { apiErrorMessage } from "@/lib/apiError";

export default function LoginPage() {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  // حالة تغيير كلمة المرور الإجباري
  const [mustChange, setMustChange] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [changingPwd, setChangingPwd] = useState(false);
  const [changeErr, setChangeErr] = useState<string | null>(null);

  const router = useRouter();

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { username: loginId, password });
      const { access_token, user, must_change_password } = res.data;
      setToken(access_token || null);
      // حفظ بيانات المستخدم والصلاحيات
      if (user) {
        setStoredUser({
          id: user.id,
          username: user.username,
          email: user.email,
          role: user.role,
          permissions: user.permissions || [],
        });
      }
      // هل يجب تغيير كلمة المرور؟
      if (must_change_password) {
        setCurrentPwd(password); // نحتفظ بكلمة المرور الحالية
        setMustChange(true);
        return;
      }
      router.replace('/dashboard');
    } catch (e: any) {
      // `apiErrorMessage` يترجم `missing_credentials` و`invalid_credentials` بنفسها
      setErr(apiErrorMessage(e, 'فشل تسجيل الدخول.'));
    } finally {
      setLoading(false);
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setChangeErr(null);
    if (newPwd.length < 8) { setChangeErr('كلمة المرور يجب أن تكون 8 أحرف على الأقل'); return; }
    if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(newPwd)) { setChangeErr('كلمة المرور يجب أن تحتوي على حرف ورقم على الأقل'); return; }
    if (newPwd !== newPwd2) { setChangeErr('كلمتا المرور غير متطابقتين'); return; }
    setChangingPwd(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPwd,
        new_password: newPwd,
      });
      router.replace('/dashboard');
    } catch (e: any) {
      setChangeErr(apiErrorMessage(e, 'فشل تغيير كلمة المرور'));
    } finally {
      setChangingPwd(false);
    }
  }

  // ── عرض نموذج تغيير كلمة المرور الإجباري ──
  if (mustChange) {
    return (
      <LandingShell title="تغيير كلمة المرور">
        <div className="mc-rise w-full max-w-md text-right">
          <div className="bg-th-surface border border-th-border rounded-2xl shadow-md p-6 md:p-8">
            <h2 className="text-lg font-bold text-th-text">تغيير كلمة المرور</h2>
            <p className="mb-4 text-sm text-th-text-muted">يجب تغيير كلمة المرور قبل المتابعة</p>
            {changeErr && (
              <div className="mb-4 rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
                {changeErr}
              </div>
            )}
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm mb-1">كلمة المرور الجديدة</label>
                <input
                  type="password"
                  className="w-full rounded-md border border-th-border-strong px-3 py-2 focus:outline-none focus:ring-2 focus:ring-th-primary/40"
                  placeholder="8 أحرف على الأقل (حرف + رقم)"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  autoComplete="new-password"
                  dir={autoDir(newPwd)}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm mb-1">تأكيد كلمة المرور الجديدة</label>
                <input
                  type="password"
                  className="w-full rounded-md border border-th-border-strong px-3 py-2 focus:outline-none focus:ring-2 focus:ring-th-primary/40"
                  placeholder="أعد كتابة كلمة المرور"
                  value={newPwd2}
                  onChange={(e) => setNewPwd2(e.target.value)}
                  autoComplete="new-password"
                  dir={autoDir(newPwd2)}
                />
              </div>
              <button
                type="submit"
                disabled={changingPwd}
                className="w-full rounded-md bg-accent-700 px-4 py-2 text-accent-foreground hover:bg-accent-800 disabled:opacity-50"
              >
                {changingPwd ? 'جاري التحديث…' : 'تغيير كلمة المرور والمتابعة'}
              </button>
            </form>
          </div>
        </div>
      </LandingShell>
    );
  }

  return (
    <LandingShell title="تسجيل الدخول">
      {/* مكان زرّ «تسجيل الدخول» في الرئيسيّة نفسه — كلّ ما فوقه وما تحته واحد */}
      <div className="mc-rise w-full max-w-md text-right">
        <div className="bg-th-surface border border-th-border rounded-2xl shadow-md p-6 md:p-8">
          {err && (
            <div className="mb-4 rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
              {err}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            {/* العنوان والحقل في سطر واحد، والحقلان بالمقاس نفسه، والكتابة من اليسار لليمين (2026-10-06) */}
            <div className="flex items-center gap-3">
              <label htmlFor="login-id" className="w-28 shrink-0 text-sm">اسم المستخدم</label>
              <input
                id="login-id"
                className="min-w-0 flex-1 rounded-md border border-th-border-strong px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-th-primary/40"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                autoComplete="username"
                dir="ltr"
              />
            </div>

            <div className="flex items-center gap-3">
              <label htmlFor="login-pw" className="w-28 shrink-0 text-sm">كلمة المرور</label>
              <input
                id="login-pw"
                type="password"
                className="min-w-0 flex-1 rounded-md border border-th-border-strong px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-th-primary/40"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                dir="ltr"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-accent-700 px-4 py-2 text-accent-foreground hover:bg-accent-800 disabled:opacity-50"
            >
              {loading ? 'جاري الدخول…' : 'دخول'}
            </button>
          </form>
        </div>
      </div>
    </LandingShell>
  );
}
